import { RuntimeWidget } from 'client/types';
import withErrorBoundary from 'client/widget-components/components/ErrorBoundary/withErrorBoundary';
import useEditor from 'client/widget-components/customHooks/useEditor';
import { useIsHydrated } from 'client/widget-components/customHooks/useIsHydrated';
import React, { useMemo, useState } from 'react';
import {
    ApiErrorResponse,
    ApiSuccessResponse,
} from '@calcom/atoms/dist/packages/platform/types/api';
import { context } from '@shared-services/context-service';
import { BookerContextProvider } from './Context';
import { Booker, LazyBookerWrapper } from './LazyBooker';
import { API_URL } from './api';
import BookerSkeleton from './components/BookerSkeleton';
import BookingMarkup from './components/BookingMarkup';
import { BookingCancelation } from './components/ConfirmationModal/BookingCancelation';
import BookingConfirmation from './components/ConfirmationModal/BookingConfirmation';
import BookingConfirmationDryRun from './components/ConfirmationModal/BookingConfirmationDryRun';
import BookingConfirmationPending from './components/ConfirmationModal/BookingConfirmationPending';
import { RescheduleModal } from './components/ConfirmationModal/RescheduleModal';
import { useBookerProps } from './hooks/useBookerProps';
import { logBookingError } from './loggingUtils';
import { CalBookingWrapper, containerQuery } from './styles';
import {
    BookerContextType,
    Booking,
    BookingStatus,
    DesignStyle,
    FeatureMetadata,
} from './types';
import { getBookerClassNames, validateBookerReceivedParams } from './utils';

export interface CalBookingProps {
    clientId: string;
    userName: string;
    eventSlug: string;
    localeCode?: any;
    apiUrl?: string;
    teamId: number;
    featureMetadata: FeatureMetadata;
    widgetId: string;
    designStyle: DesignStyle | null;
}

const DEFAULT_LOCALE = 'en';
const RuntimeCalBooking: RuntimeWidget = (props: CalBookingProps) => {
    const [isLoading, setIsLoading] = useState(true);
    const [rescheduleUid, setRescheduleUid] = useState<string | null>(null);
    const [cancelBookingId, setCancelBookingId] = useState<string | null>(null);
    const [confirmationData, setConfirmationData] = useState<Booking | null>(
        null
    );
    const [editor] = useState(useEditor());
    const hydrated = useIsHydrated();

    const {
        localeCode = DEFAULT_LOCALE,
        apiUrl = API_URL,
        clientId,
        featureMetadata,
        widgetId,
        designStyle: designStyleProp,
    } = props;

    const { eventSlug, teamId, userName } = useBookerProps({
        ...props,
        setRescheduleUid,
        setCancelBookingId,
    });

    const isBookingActive = !!eventSlug;
    const designStyle = designStyleProp || DesignStyle.SITE_THEME;

    const bookerClassNames = useMemo(
        () => getBookerClassNames(designStyle),
        [designStyle]
    );

    const bookerContext: BookerContextType = {
        eventSlug,
        teamId,
        userName,
        rescheduleUid,
        cancelBookingId,
        widgetId,
        designStyle,
        localeCode,
        apiUrl,
        clientId,
        ...featureMetadata,
    };

    // validate all required props are present for booker atom when booking is active
    validateBookerReceivedParams(bookerContext, isBookingActive);

    if (!isBookingActive && !editor.isInPreviewOrEditor()) {
        return null;
    }

    const clearConfirmationData = () => {
        setConfirmationData(null);
    };

    const rescheduleBooking = () => {
        if (!confirmationData?.uid) {
            return;
        }
        setRescheduleUid(confirmationData.uid);
        clearConfirmationData();
    };

    const onBookingSuccess = (res: ApiSuccessResponse<any>) => {
        setRescheduleUid(null);
        // Extract booking data from the API response structure
        const bookingData = res.data as Booking;
        setConfirmationData(bookingData);
    };

    const isBookingDryRun =
        editor.isInPreviewOrEditor() ||
        !featureMetadata.isNativeBookingPublishingAvailable;

    const showDryRunConfirmation = confirmationData && isBookingDryRun;
    const showConfirmation =
        confirmationData &&
        !isBookingDryRun &&
        confirmationData?.status === BookingStatus.ACCEPTED;
    const showPendingConfirmation =
        confirmationData &&
        !isBookingDryRun &&
        confirmationData?.status === BookingStatus.PENDING;
    // We want to render the booker only after the component is hydrated on client side since it's a lazy loaded component and we want to keep the server and client rendering consistent.
    const shouldRenderBooker = hydrated && isBookingActive;
    const shouldRenderLoadingSkeleton = isBookingActive && isLoading;
    const shouldRenderBookingMarkup = !isBookingActive;
    const showRescheduleModal = !isBookingDryRun && rescheduleUid && !isLoading;

    const calBookerWrapperClassNames: string[] = [designStyle];
    if (isBookingActive) {
        calBookerWrapperClassNames.push(
            isLoading ? 'booker-loading' : 'booker-ready'
        );
    }

    const commonBookerProps = {
        eventSlug,
        customClassNames: bookerClassNames,
        onCreateBookingSuccess: onBookingSuccess,
        onCreateBookingError: (resOrError: ApiErrorResponse | Error) => {
            logBookingError('Failed to create booking inside Booker atom', {
                ...(resOrError instanceof Error
                    ? {
                          errorMessage: resOrError.message,
                          additionalInfoToLog: bookerContext,
                      }
                    : {
                          errorMessage: resOrError.error.message,
                          errorCode: resOrError.error.code,
                          errorDetails: resOrError.error.details,
                          additionalInfoToLog: bookerContext,
                      }),
            });
        },
        onReserveSlotError: (res: ApiErrorResponse) => {
            logBookingError('Failed to reserve slot inside Booker atom', {
                errorMessage: res.error.message,
                errorCode: res.error.code,
                errorDetails: res.error.details,
                additionalInfoToLog: bookerContext,
            });
        },
        onDeleteSlotError: (res: ApiErrorResponse) => {
            logBookingError('Failed to delete slot inside Booker atom', {
                errorMessage: res.error.message,
                errorCode: res.error.code,
                errorDetails: res.error.details,
                additionalInfoToLog: bookerContext,
            });
        },
        showNoAvailabilityDialog: false,
        roundRobinHideOrgAndTeam: true,
        metadata: {
            originalBookingPath: context.isBrowser
                ? window.location.pathname
                : '',
        },
    };

    return (
        <CalBookingWrapper
            data-auto='ssr-cal-booking-widget'
            className={calBookerWrapperClassNames.join(' ')}
        >
            <BookerContextProvider {...bookerContext}>
                <style
                    dangerouslySetInnerHTML={{
                        __html: containerQuery(widgetId),
                    }}
                />
                <div className='booker-wrapper'>
                    {shouldRenderBooker && (
                        <LazyBookerWrapper
                            clientId={clientId}
                            apiUrl={apiUrl}
                            localeCode={localeCode}
                        >
                            <Booker
                                key={
                                    confirmationData
                                        ? 'booker-confirmed'
                                        : 'booker-initial'
                                }
                                {...(teamId
                                    ? { isTeamEvent: true, teamId }
                                    : { username: userName })}
                                isBookingDryRun={isBookingDryRun}
                                onBookerStateChange={(bookerState) => {
                                    if (
                                        bookerState.state === 'selecting_time'
                                    ) {
                                        setIsLoading(false);
                                    }
                                }}
                                {...commonBookerProps}
                            />
                            {showRescheduleModal && (
                                <RescheduleModal
                                    bookerProps={{
                                        bookingUid: rescheduleUid,
                                        isTeamEvent: true,
                                        teamId,
                                        ...commonBookerProps,
                                    }}
                                    onClose={() => setRescheduleUid(null)}
                                />
                            )}
                        </LazyBookerWrapper>
                    )}
                    {showConfirmation && (
                        <BookingConfirmation
                            confirmationData={confirmationData}
                            closeConfirmation={clearConfirmationData}
                            rescheduleBooking={rescheduleBooking}
                            setCancelBookingId={setCancelBookingId}
                        />
                    )}
                    {showPendingConfirmation && (
                        <BookingConfirmationPending
                            confirmationData={confirmationData}
                            closeConfirmation={clearConfirmationData}
                            rescheduleBooking={rescheduleBooking}
                            setCancelBookingId={setCancelBookingId}
                        />
                    )}
                    {showDryRunConfirmation && (
                        <BookingConfirmationDryRun
                            closeConfirmation={clearConfirmationData}
                        />
                    )}
                    {cancelBookingId && (
                        <BookingCancelation
                            bookingUid={cancelBookingId}
                            closeCancelation={() => setCancelBookingId(null)}
                        />
                    )}
                    {shouldRenderLoadingSkeleton && <BookerSkeleton />}
                    {shouldRenderBookingMarkup && <BookingMarkup />}
                </div>
            </BookerContextProvider>
        </CalBookingWrapper>
    );
};

export default withErrorBoundary({
    Comp: RuntimeCalBooking,
    logProps: true,
    componentName: 'CalBooking',
    additionalInfoToLog: {
        tag: 'booking',
    },
});
